import React from 'react';
import { Link } from 'react-router-dom';
import { defaultDataNotCollected, defaultThirdPartyServices } from '../data/appPageData';

const GenericPrivacyPage = ({ appData }) => {
  const dataNotCollected = appData.dataNotCollected || defaultDataNotCollected;
  const thirdPartyServices = appData.thirdPartyServices || defaultThirdPartyServices;
  const hasAccount = !appData.noAccount;

  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Legal</span>
          <h1 className="legal-page__title">Privacy Policy</h1>
          <p className="legal-page__subtitle">
            {appData.name} by Wolf Technologies Ltd
          </p>
          <p className="legal-page__date">Last updated: {appData.lastUpdated}</p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>{appData.intro}</p>
            <p>
              We encourage you to read this policy in full. If you have any questions, please contact
              us at{' '}
              <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Data We Collect</h2>
            <ul>
              {appData.dataCollected.map((item, i) => (
                <li key={i}>
                  <strong>{item.label}</strong> — {item.detail}.
                </li>
              ))}
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Data We Do NOT Collect</h2>
            <ul>
              {dataNotCollected.map((item, i) => (
                <li key={i}><strong>{item}</strong></li>
              ))}
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. How We Use Your Data</h2>
            <p>Your data is used solely to provide and improve the {appData.name} service:</p>
            <ul>
              {appData.dataUsage.map((item, i) => (
                <li key={i}>{item}.</li>
              ))}
            </ul>
            <p>
              We do <strong>not</strong> use your data for advertising, profiling, or any purpose
              unrelated to providing the {appData.name} service.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Data Storage & Security</h2>
            {hasAccount ? (
              <>
                <p>
                  All data is stored on Amazon Web Services (AWS) in the eu-west-2 (London) region
                  within the United Kingdom.
                </p>
                <ul>
                  <li><strong>Encryption at rest</strong> — all data stored in AWS DynamoDB is encrypted using AWS-managed encryption keys (AES-256).</li>
                  <li><strong>Encryption in transit</strong> — all communication between the app and our servers uses TLS 1.2 or higher (HTTPS).</li>
                  <li><strong>Authentication</strong> — user accounts are managed through AWS Cognito with secure password hashing and token management.</li>
                  <li><strong>Access controls</strong> — your data is partitioned by your unique user ID. No other user can access your data.</li>
                </ul>
              </>
            ) : (
              <p>
                {appData.name} stores all data locally on your device. No personal data is transmitted
                to external servers.
              </p>
            )}
          </section>

          {thirdPartyServices.length > 0 && (
            <section className="legal-section">
              <h2>6. Third-Party Services</h2>
              <ul>
                {thirdPartyServices.map((service, i) => (
                  <li key={i}>
                    <strong>{service.name}</strong> — {service.purpose}.
                  </li>
                ))}
              </ul>
              <p>
                We do not use any analytics services, advertising SDKs, or third-party tracking tools
                that collect personal data.
              </p>
            </section>
          )}

          <section className="legal-section">
            <h2>{thirdPartyServices.length > 0 ? '7' : '6'}. Data Sharing</h2>
            <p>
              <strong>We do not sell, rent, or share your personal data with any third party</strong> for
              marketing or advertising purposes.
            </p>
            {hasAccount && (
              <p>
                The only third parties that receive any data are the service providers listed above,
                strictly for operating the service. Data may also be disclosed if required by law or
                valid legal process.
              </p>
            )}
          </section>

          <section className="legal-section">
            <h2>{thirdPartyServices.length > 0 ? '8' : '7'}. Data Retention & Deletion</h2>
            {hasAccount ? (
              <ul>
                <li>Your data is retained for as long as your account is active.</li>
                <li>You can delete your account and all associated data at any time through Settings in the app.</li>
                <li>Upon deletion, all your data is permanently removed from our servers. This action is irreversible.</li>
                <li>Cancelling a subscription does not delete your account or data.</li>
              </ul>
            ) : (
              <p>
                All data is stored locally on your device. You can clear your data at any time by
                clearing the app data through your device settings or uninstalling the app.
              </p>
            )}
          </section>

          <section className="legal-section">
            <h2>{thirdPartyServices.length > 0 ? '9' : '8'}. Your Rights (GDPR)</h2>
            <p>
              Under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act
              2018, you have the following rights:
            </p>
            <ul>
              <li><strong>Access</strong> — request a copy of the personal data we hold about you.</li>
              <li><strong>Rectification</strong> — request correction of inaccurate personal data.</li>
              <li><strong>Erasure</strong> — request deletion of your personal data.</li>
              <li><strong>Data portability</strong> — request your data in a structured, machine-readable format.</li>
              <li><strong>Restriction</strong> — request that we limit how we use your data.</li>
              <li><strong>Objection</strong> — object to the processing of your personal data.</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>. We will
              respond within 30 days, as required by law.
            </p>
          </section>

          {appData.additionalPrivacySections && appData.additionalPrivacySections.map((section, i) => (
            <section key={i} className="legal-section">
              <h2>{(thirdPartyServices.length > 0 ? 10 : 9) + i}. {section.title}</h2>
              <p>{section.content}</p>
            </section>
          ))}

          <section className="legal-section">
            <h2>{(thirdPartyServices.length > 0 ? 10 : 9) + (appData.additionalPrivacySections?.length || 0)}. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. If we make significant changes, we
              will notify you through the app or by email. The "Last updated" date at the top of this
              page will be revised.
            </p>
          </section>

          <section className="legal-section">
            <h2>{(thirdPartyServices.length > 0 ? 11 : 10) + (appData.additionalPrivacySections?.length || 0)}. Contact</h2>
            <p>
              If you have any questions about this Privacy Policy or your personal data, please
              contact us:
            </p>
            <div className="legal-contact">
              <p><strong>Wolf Technologies Ltd</strong></p>
              <p>United Kingdom</p>
              <p>
                Email:{' '}
                <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
              </p>
            </div>
            <p>We aim to respond to all enquiries within 48 hours.</p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default GenericPrivacyPage;
